import type { Settings } from "./settings";
import { chooseEngineModule, type GPUProbe } from "./engine-backend";
export interface Diagnostics {
  fps: number;
  frameMs?: number;
  p95FrameMs?: number;
  drawCalls?: number;
  triangles?: number;
  ready: boolean;
  rendererReady?: boolean;
  renderCompletedSceneFrames?: number;
  renderPendingPipelines?: number;
  renderPendingAssets?: number;
  assetErrors?: string[];
  renderer?: string;
  tick?: number;
  phase?: string;
  localPlayer?: {
    position: { x: number; y: number };
    alive: boolean;
    last_input_sequence: number;
  } | null;
  cameraTargetPlayer?: number | null;
  predictedPosition?: { x: number; y: number } | null;
  inputSequence?: number;
  castSequence?: number;
  lastLocalCast?: number | null;
  processedEventCount?: number;
  networkRttMs?: number | null;
  predictedTicks?: number;
  inputHistoryLength?: number;
  inputSynchronizing?: boolean;
  inputSyncReason?: "cold-start" | "missed-deadlines" | null;
  inputSyncGeneration?: number;
  inputSyncProbeSequence?: number | null;
  inputSyncProbeTick?: number | null;
  inputSyncConfirmedSequence?: number | null;
  sceneEntities?: {
    fireballs: number;
    trails: number;
    shields: number;
    domes: number;
    meteors: number;
    sparks: number;
    flashes: number;
  };
}
export interface EngineAPI {
  default?: () => Promise<unknown>;
  set_snapshot?: (json: string) => void;
  set_snapshot_with_age?: (json: string, ageMs: number) => void;
  set_local_player?: (id: number) => void;
  set_mode?: (mode: string) => void;
  set_follow_player?: (id: number) => void;
  set_settings?: (json: string) => void;
  set_sim_config?: (json: string) => void;
  set_input_enabled?: (enabled: boolean) => void;
  set_movement_keys?: (keys: number) => void;
  set_network_rtt_ms?: (rttMs: number) => void;
  clear_network_rtt_ms?: () => void;
  set_prediction_policy?: (matchId: string, json: string) => boolean;
  drain_inputs?: () => string;
  set_input_callback?: (callback: ((raw: string) => void) | undefined) => void;
  get_diagnostics?: () => string;
}
/** One owner for the WASM lifecycle. No simulation is performed by the web shell. */
export class Engine {
  constructor(
    private loadModule: (path: string) => Promise<EngineAPI> = (path) =>
      import(/* @vite-ignore */ path),
  ) {}
  private api?: EngineAPI;
  private initialized = false;
  private pendingSnapshot?: { raw: string; receivedAt: number };
  private localPlayer = 0;
  private mode = "demo";
  private enabled = false;
  private heldMovement = 0;
  private settings?: Settings;
  private follow = 0;
  private config?: string;
  private rttMs?: number;
  private matchPolicy?: { matchId: string; json: string };
  private inputHandler?: (raw: string) => void;
  private directInputs = false;
  async start(): Promise<void> {
    if (!this.initialized) {
      const path = await chooseEngineModule(
        (navigator as unknown as { gpu?: GPUProbe }).gpu,
      );
      document.documentElement.dataset.renderer = path.includes("webgl")
        ? "webgl2"
        : "webgpu";
      const module = await this.loadModule(path);
      await module.default?.();
      this.api = module;
      this.initialized = true;
      this.configureInputCallback();
      module.set_local_player?.(this.localPlayer);
      module.set_mode?.(this.mode);
      module.set_input_enabled?.(this.enabled);
      module.set_movement_keys?.(this.heldMovement);
      module.set_follow_player?.(this.follow);
      if (this.rttMs !== undefined) module.set_network_rtt_ms?.(this.rttMs);
      this.applyPredictionPolicy();
      if (this.settings) module.set_settings?.(JSON.stringify(this.settings));
      if (this.config) module.set_sim_config?.(this.config);
      this.applySnapshot();
    }
    if (!this.api?.get_diagnostics) return;
    await new Promise<void>((resolve, reject) => {
      const started = performance.now();
      const check = () => {
        const info = this.diagnostics();
        if (info?.ready) {
          resolve();
          return;
        }
        if (info?.assetErrors?.length) {
          reject(
            new Error(
              `Arena assets could not load: ${info.assetErrors.join(", ")}`,
            ),
          );
          return;
        }
        if (performance.now() - started > 60_000) {
          reject(
            new Error("The arena took too long to load. Reload to try again."),
          );
          return;
        }
        setTimeout(check, 100);
      };
      check();
    });
  }
  snapshot(rawSnapshot: string, receivedAt = performance.now()): void {
    this.pendingSnapshot = {
      raw: rawSnapshot,
      receivedAt: Number.isFinite(receivedAt) ? receivedAt : performance.now(),
    };
    this.applySnapshot();
  }
  private applySnapshot(): void {
    if (!this.api || !this.pendingSnapshot) return;
    const { raw, receivedAt } = this.pendingSnapshot;
    // Decoding and WASM startup can queue a world for hundreds of milliseconds.
    // Its tick must retain that age when native code schedules future controls.
    if (this.api.set_snapshot_with_age)
      this.api.set_snapshot_with_age(raw, Math.max(0, performance.now() - receivedAt));
    else this.api.set_snapshot?.(raw);
  }
  player(id: number | null): void {
    this.localPlayer = id ?? 0;
    this.api?.set_local_player?.(this.localPlayer);
  }
  setMode(mode: "demo" | "match" | "observe"): void {
    this.mode = mode;
    this.api?.set_mode?.(mode);
  }
  input(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    this.api?.set_input_enabled?.(enabled);
  }
  movementKeys(keys: number): void {
    this.heldMovement = keys & 15;
    this.api?.set_movement_keys?.(this.heldMovement);
  }
  configure(settings: Settings): void {
    this.settings = settings;
    this.api?.set_settings?.(JSON.stringify(settings));
  }
  simConfig(config: unknown): void {
    if (!config) return;
    this.config = JSON.stringify(config);
    this.api?.set_sim_config?.(this.config);
  }
  followPlayer(id: number | null): void {
    this.follow = id ?? 0;
    this.api?.set_follow_player?.(this.follow);
  }
  inputs(): string | undefined {
    // New engines transfer input ownership synchronously. The RAF reader is
    // retained solely for older cached engines and must not resend direct input.
    if (this.directInputs) return undefined;
    return this.api?.drain_inputs?.();
  }
  onInputs(handler: ((raw: string) => void) | undefined): void {
    this.inputHandler = handler;
    this.configureInputCallback();
  }
  private configureInputCallback(): void {
    const install = this.api?.set_input_callback;
    if (!install) return;
    this.directInputs = this.inputHandler !== undefined;
    install(this.directInputs ? (raw) => this.inputHandler?.(raw) : undefined);
  }
  networkTiming(rttMs: number | null): void {
    if (rttMs === null) {
      this.rttMs = undefined;
      this.api?.clear_network_rtt_ms?.();
      return;
    }
    if (!Number.isFinite(rttMs) || rttMs < 0 || rttMs > 2000) return;
    this.rttMs = rttMs;
    this.api?.set_network_rtt_ms?.(rttMs);
  }
  predictionPolicy(matchId: string, policy: unknown): void {
    const ticks = (policy as { inputDelayTicks?: unknown } | null)?.inputDelayTicks;
    if (!matchId || !policy || typeof policy !== "object" ||
        Object.keys(policy).length !== 1 || typeof ticks !== "number" ||
        !Number.isInteger(ticks) || ticks < 0 || ticks > 3)
      throw new Error("Invalid match prediction policy");
    const json = JSON.stringify({ inputDelayTicks: ticks });
    if (this.matchPolicy?.matchId === matchId && this.matchPolicy.json !== json)
      throw new Error("An active match cannot change its prediction timing");
    this.matchPolicy = { matchId, json };
    this.applyPredictionPolicy();
  }
  private applyPredictionPolicy(): void {
    if (!this.api || !this.matchPolicy) return;
    if (!this.api.set_prediction_policy ||
        !this.api.set_prediction_policy(this.matchPolicy.matchId, this.matchPolicy.json))
      throw new Error("The renderer cannot apply this match prediction policy");
  }
  diagnostics(): Diagnostics | undefined {
    try {
      const json = this.api?.get_diagnostics?.();
      return json ? (JSON.parse(json) as Diagnostics) : undefined;
    } catch {
      return undefined;
    }
  }
}
